import { useState } from 'react';
import * as XLSX from 'xlsx';
import { adminHeaders } from '../../lib/adminAuth';
import './BookingImportModal.css';

import { tx } from '../../lib/adminI18n';
// ------------------------------------------------------------
// Booking.com 予約取込モーダル
//   物件を選び、Booking.com の予約一覧ファイル(.xls/.xlsx/.csv)を選択 → ブラウザでパース →
//   行配列(JSON)を /api/admin/reservations/import-booking に送信して一括登録する。
//   Booking.com の日本語ヘッダー列をDBの予約フィールドにマッピングする。
// ------------------------------------------------------------

interface PropertyOpt { id: string; name: string; }

interface Props {
  properties: PropertyOpt[];
  onClose: () => void;
  onImported: () => void; // 取込成功後に一覧を再読み込みさせる
}

interface ImportRow {
  ota_reservation_code: string;
  guest_name: string;
  checkin_date: string;
  checkout_date: string;
  guest_count: number;
  total_price: string;
  guest_country: string;
  status: string;
  cancelled_at: string;
}

interface ImportResult {
  ok: boolean;
  imported: number;
  skipped: number;
  errored: number;
  total: number;
  results: Array<{ row: number; code: string; status: string; reason?: string }>;
}

// Booking.com のヘッダー表記ゆれを吸収するための候補マップ（日本語/英語）。
const HEADER_ALIASES: Record<keyof ImportRow, string[]> = {
  ota_reservation_code: ['予約番号', 'Book number', 'Booking number', 'Reservation number'],
  guest_name: ['宿泊者氏名', 'Guest name(s)', 'Guest name', 'Guest names'],
  checkin_date: ['チェックイン', 'Check-in', 'Arrival'],
  checkout_date: ['チェックアウト', 'Check-out', 'Departure'],
  guest_count: ['人数', 'People', 'Persons', 'Guests'],
  total_price: ['料金', 'Price', 'Amount', 'Rate'],
  guest_country: ['Booker country', '予約者の国', 'Country'],
  status: ['ステータス', 'Status'],
  cancelled_at: ['キャンセル日', 'Cancelled on', 'Cancellation date'],
};

// 日付セル → 'YYYY-MM-DD'。Excelのシリアル値/Dateオブジェクト/文字列に対応。
function toYmd(v: any): string {
  if (v == null || v === '') return '';
  if (v instanceof Date && !isNaN(v.getTime())) {
    const y = v.getFullYear(), m = String(v.getMonth() + 1).padStart(2, '0'), d = String(v.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const s = String(v).trim();
  // 2026-11-01 / 2026/11/01 → 正規化
  const m = s.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  return s;
}

function pickHeader(headers: string[], aliases: string[]): number {
  const norm = (x: string) => x.replace(/\s+/g, '').toLowerCase();
  const wanted = aliases.map(norm);
  for (let i = 0; i < headers.length; i++) {
    if (wanted.includes(norm(headers[i] || ''))) return i;
  }
  return -1;
}

export default function BookingImportModal({ properties, onClose, onImported }: Props) {
  const [propertyId, setPropertyId] = useState('');
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [parseMsg, setParseMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);

  const onFile = async (file: File) => {
    setParseMsg(null); setResult(null); setRows([]); setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      // ヘッダー行を含む2次元配列として取得
      const aoa: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' });
      if (aoa.length < 2) { setParseMsg({ ok: false, text: tx('bki.9') }); return; }
      const headers = (aoa[0] || []).map((x) => String(x ?? '').trim());

      const idx: Record<keyof ImportRow, number> = {} as any;
      (Object.keys(HEADER_ALIASES) as (keyof ImportRow)[]).forEach((k) => {
        idx[k] = pickHeader(headers, HEADER_ALIASES[k]);
      });
      // 必須列チェック
      const missing: string[] = [];
      (['checkin_date', 'checkout_date', 'guest_count'] as (keyof ImportRow)[]).forEach((k) => {
        if (idx[k] < 0) missing.push(HEADER_ALIASES[k][0]);
      });
      if (missing.length) {
        setParseMsg({ ok: false, text: tx('bki.10', { p0: missing.join(' / ') }) });
        return;
      }

      const out: ImportRow[] = [];
      for (let i = 1; i < aoa.length; i++) {
        const r = aoa[i] || [];
        const get = (k: keyof ImportRow) => (idx[k] >= 0 ? r[idx[k]] : '');
        const checkin = toYmd(get('checkin_date'));
        const checkout = toYmd(get('checkout_date'));
        if (!checkin && !checkout) continue; // 空行スキップ
        out.push({
          ota_reservation_code: String(get('ota_reservation_code') ?? '').trim(),
          guest_name: String(get('guest_name') ?? '').trim(),
          checkin_date: checkin,
          checkout_date: checkout,
          guest_count: Math.floor(Number(get('guest_count')) || 0),
          total_price: String(get('total_price') ?? '').trim(),
          guest_country: String(get('guest_country') ?? '').trim(),
          status: String(get('status') ?? '').trim(),
          cancelled_at: toYmd(get('cancelled_at')),
        });
      }
      if (!out.length) { setParseMsg({ ok: false, text: tx('bki.11') }); return; }
      setRows(out);
      setParseMsg({ ok: true, text: tx('bki.12', { p0: out.length }) });
    } catch (e: any) {
      setParseMsg({ ok: false, text: tx('bki.13', { p0: e?.message || e }) });
    }
  };

  const submit = async () => {
    if (!propertyId) { setParseMsg({ ok: false, text: tx('bki.14') }); return; }
    if (!rows.length) { setParseMsg({ ok: false, text: tx('bki.15') }); return; }
    setBusy(true); setResult(null);
    try {
      const res = await fetch('/api/admin/reservations/import-booking', {
        method: 'POST',
        headers: adminHeaders({ 'Content-Type': 'application/json' }),
        credentials: 'include',
        body: JSON.stringify({ property_id: propertyId, rows }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { setParseMsg({ ok: false, text: d.error || tx('bki.16') }); return; }
      setResult(d as ImportResult);
      setParseMsg(null);
      onImported();
    } catch (e: any) {
      setParseMsg({ ok: false, text: e?.message || tx('bki.16') });
    } finally { setBusy(false); }
  };

  return (
    <div className="bimp-overlay" onClick={() => !busy && onClose()}>
      <div className="bimp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="bimp-head">
          <h2>{tx('bki.17')}</h2>
          <button className="bimp-x" onClick={onClose} disabled={busy}>×</button>
        </div>

        <p className="bimp-sub">
          {tx('bki.18')}
          {tx('bki.19')}
          {tx('bki.20')}
        </p>

        <div className="bimp-field">
          <label>{tx('bki.21')}</label>
          <select value={propertyId} onChange={(e) => setPropertyId(e.target.value)} disabled={busy}>
            <option value="">{tx('bki.22')}</option>
            {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </div>

        <div className="bimp-field">
          <label>{tx('bki.23')}</label>
          <input
            type="file"
            accept=".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
            disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }}
          />
          {fileName && <small className="bimp-filename">{fileName}</small>}
        </div>

        {parseMsg && <div className={parseMsg.ok ? 'bimp-msg bimp-msg-ok' : 'bimp-msg bimp-msg-err'}>{parseMsg.text}</div>}

        {result && (
          <div className="bimp-result">
            <div className="bimp-result-summary">
              <span className="bimp-chip bimp-chip-ok">{tx('bki.24')} {result.imported}</span>
              <span className="bimp-chip bimp-chip-skip">{tx('bki.25')} {result.skipped}</span>
              <span className="bimp-chip bimp-chip-err">{tx('bki.26')} {result.errored}</span>
              <span className="bimp-chip">{tx('bki.27')} {result.total}</span>
            </div>
            {(result.skipped > 0 || result.errored > 0) && (
              <div className="bimp-result-detail">
                <table>
                  <thead><tr><th>{tx('bki.28')}</th><th>{tx('bki.0')}</th><th>{tx('bki.29')}</th><th>{tx('bki.30')}</th></tr></thead>
                  <tbody>
                    {result.results.filter((r) => r.status !== 'imported').map((r, i) => (
                      <tr key={i}>
                        <td>{r.row}</td>
                        <td>{r.code || '—'}</td>
                        <td className={r.status === 'error' ? 'st-err' : 'st-skip'}>{r.status === 'error' ? tx('bki.26') : tx('bki.25')}</td>
                        <td>{r.reason || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="bimp-actions">
          {result ? (
            <button className="bimp-btn bimp-btn-primary" onClick={onClose}>{tx('bki.31')}</button>
          ) : (
            <>
              <button className="bimp-btn bimp-btn-ghost" onClick={onClose} disabled={busy}>{tx('bki.32')}</button>
              <button className="bimp-btn bimp-btn-primary" onClick={submit} disabled={busy || !rows.length || !propertyId}>
                {busy ? tx('bki.33') : tx('bki.34', { p0: rows.length ? tx('bki.count', { n: rows.length }) : '' })}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
